import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initNativeShell } from "@/lib/nativeShell";

// Kick off native shell setup (no-op on plain web)
initNativeShell();

// Silence the harmless "ResizeObserver loop completed with undelivered notifications"
// error that Radix Popover / cmdk sometimes throws when the popover resizes mid-render.
// It's a known, non-critical browser quirk — safe to swallow. We use capture:true so
// this handler runs BEFORE the CRA/webpack dev overlay's error handler.
const RESIZE_OBSERVER_ERR = "ResizeObserver loop";
const swallow = (e) => {
    // Silence resize-observer noise — check every place browsers surface it.
    const msgs = [
        e?.message,
        e?.error?.message,
        e?.reason?.message,
        typeof e?.reason === "string" ? e.reason : "",
    ];
    if (msgs.some((m) => typeof m === "string" && m.includes(RESIZE_OBSERVER_ERR))) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        // Also stop CRA's dev overlay from picking it up in the same tick.
        e.stopPropagation?.();
        return true;
    }
    // Silence uncaught axios errors from any missing try/catch — they already surface
    // as toasts where handled; the CRA red overlay just scares users otherwise.
    const reason = e?.reason;
    if (reason && (reason.isAxiosError || reason.name === "AxiosError")) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        console.warn("[unhandled axios] " + (reason.message || "request failed"));
        return true;
    }
    return false;
};
window.addEventListener("error", swallow, { capture: true });
window.addEventListener("unhandledrejection", swallow, { capture: true });

// Also patch console.error so the CRA red overlay (which reads console output) skips it.
const origConsoleError = window.console.error;
window.console.error = function (...args) {
    if (args.some((a) => {
        const s = typeof a === "string" ? a : a?.message || "";
        return typeof s === "string" && s.includes(RESIZE_OBSERVER_ERR);
    })) return;
    return origConsoleError.apply(this, args);
};

// Belt-and-braces: shim ResizeObserver itself so its callback errors get swallowed
// before they ever surface to `window.onerror`. This is the only path that reliably
// silences the CRA dev overlay across React 18 + Radix + webpack-dev-server.
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
    const NativeRO = window.ResizeObserver;
    class PatchedRO extends NativeRO {
        constructor(cb) {
            super((...args) => {
                window.requestAnimationFrame(() => {
                    try { cb(...args); } catch (err) {
                        const m = (err && err.message) || "";
                        if (typeof m === "string" && m.includes(RESIZE_OBSERVER_ERR)) return;
                        throw err;
                    }
                });
            });
        }
    }
    window.ResizeObserver = PatchedRO;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
